import { Component } from '@angular/core';
import { Validators } from '@angular/forms';
import { SimpleSearchComponent } from 'src/app/base/components/abstract/simple/simple-search.component';
import { DataAction } from 'src/app/base/enum/data-action.enum';
import { DialogReturnData } from 'src/app/base/models/dialog-return-data.model';
import { Option } from 'src/app/base/models/option.model';
import { TableColumn } from 'src/app/base/models/table-column.model';
import { takeUntil, Subject, switchMap, filter, take, lastValueFrom, forkJoin } from 'rxjs';
import { CommonService } from '../../services/common.service';
import { AuthService } from 'src/app/core/services/auth.service';

interface ItemOption extends Option {
  description?: string;
  brand?: string;
}

@Component({
  selector: 'app-internal-transfer-price-inquiry',
  templateUrl: './internal-transfer-price-inquiry.component.html',
  styleUrls: ['./internal-transfer-price-inquiry.component.scss']
})
export class InternalTransferPriceInquiryComponent extends SimpleSearchComponent {
  override rowDataKeyName: string = 'price';
  override rowDataDeleteHintFieldName: string = 'price';
  override componentClassName: string = 'InternalTransferPriceInquiryComponent';

  override showActions: boolean = false;
  override showNoDataFoundMessage: boolean = false;

  readonly appTypeOptions: Option[] = [
    { id: '1', label: '1-AJB', value: '1' },
    { id: '2', label: '2-IR/ISO', value: '2' },
    { id: '4', label: '4-RMA(AJB Return To B)', value: '4' },
  ];

  // Request OU AutoComplete
  reqOuSuggestions: Option[] = [];
  private readonly reqOuSubject$ = new Subject<string | undefined>();

  // Inv OU AutoComplete
  invOuSuggestions: Option[] = [];
  private readonly invOuSubject$ = new Subject<string | undefined>();

  // CTMR AutoComplete
  ctmrSuggestions: Option[] = [];
  private readonly ctmrSubject$ = new Subject<string>();

  // Item Number AutoComplete
  itemSuggestions: ItemOption[] = [];
  private readonly itemSubject$ = new Subject<string | undefined>();

  // INV Org suggestions
  invOrgSuggestions: Option[] = [];

  // End Customer suggestions
  endCustomerSuggestions: string[] = [];

  queryId: string = '';

  private resolvedInventoryItemId: number = 0;
  private resolvedBrand: string = '';

  constructor(
    private readonly commonService: CommonService,
    private readonly authService: AuthService
  ) {
    super();
  }

  override initFormGroup(): void {
    const today = new Date();
    this.formGroup = this.formBuilder.nonNullable.group({
      applicationType: ['1', Validators.required],
      requestDate: [today, Validators.required],
      reqOu: ['', Validators.required],
      invOu: ['', Validators.required],
      ctmr: ['', Validators.required],
      itemNumber: ['', Validators.required],
      itemDesc: [{ value: '', disabled: true }],
      orderNumber: [''],
      invOrg: [{ value: '', disabled: true }],
      endCustomer: [{ value: '', disabled: true }],
      projectName: [''],
    });
  }

  override initTableColumns(): void {
    this.tableColumns = [
      {
        header: 'field.internal-transfer-price',
        field: 'price',
        sortable: true,
        numberField: true,
      },
      {
        header: 'field.valid-from',
        field: 'startDate',
        sortable: true,
      },
      {
        header: 'field.valid-to',
        field: 'endDate',
        sortable: true,
      },
    ];
  }

  override async beforeQuery(): Promise<boolean> {
    return true;
  }

  override async doQuery(): Promise<boolean> {
    const reqOuValue = this.formControl('reqOu').value;
    const invOuValue = this.formControl('invOu').value;
    const ctmrValue = this.formControl('ctmr').value;
    const itemValue = this.formControl('itemNumber').value;
    const requestDateValue: Date = this.formControl('requestDate').value;
    const orderNumber: string = this.formControl('orderNumber').value ?? '';
    const endCustomer: string = this.formControl('endCustomer').value ?? '';
    const invOrg: string = this.formControl('invOrg').value ?? '';
    const projectName: string = this.formControl('projectName').value ?? '';

    const reqOuId = reqOuValue?.id ? Number(reqOuValue.id) : 0;
    const invOuId = invOuValue?.id ? Number(invOuValue.id) : 0;
    const custId = ctmrValue?.id ? Number(ctmrValue.id) : 0;
    const parsedItemId = Number(itemValue?.id);
    const inventoryItemId = (itemValue?.id != null && !isNaN(parsedItemId)) ? parsedItemId : 0;
    const empNo: string = this.authService.getUser()?.username ?? '';
    const language = this.translateService.currentLang?.startsWith('zh') ? 'ZHT' : 'US';
    const uuid = crypto.randomUUID();

    // Format date as YYYYMMDD
    const year = requestDateValue.getFullYear();
    const month = String(requestDateValue.getMonth() + 1).padStart(2, '0');
    const day = String(requestDateValue.getDate()).padStart(2, '0');
    const requestDate = `${year}${month}${day}`;

    try {
      const res = await lastValueFrom(
        this.commonService.queryInternalTransferPriceInq({
          applyType: this.formControl('applicationType').value,
          custId,
          ...(orderNumber ? { docNo: orderNumber } : {}),
          empNo,
          ...(endCustomer ? { endCustomer } : {}),
          inventoryItemId,
          language,
          invOuId,
          ...(invOrg ? { organizationCode: invOrg } : {}),
          ...(projectName ? { projectName } : {}),
          reqOuId,
          requestDate,
          uuid,
        })
      );
      this.queryId = res.queryId ?? '';
      this.detailDatas = (res.responseData ?? []).map(item => ({
        ...item,
        price: item.price ? item.price.trim() : item.price,
      }));
    } catch {
      this.detailDatas = [];
    }
    return true;
  }

  override afterSelectedRowDataCountChange(selectedDataCount: number): void {
    return;
  }
  override afterRowDataCountChange(rowDataCount: number): void {
    return;
  }
  override initExtraDetailMenu(): void {
    return;
  }
  override setExtraDetailMenuStatus(): void {
    return;
  }
  override initDialogConfig(dataAction: DataAction): void {
    return;
  }
  override initExtraRowActionMenu(): void {
    return;
  }
  override setExtraRowActionMenuStatus(): void {
    return;
  }
  override doExtraDetailDialogClose(returnData: DialogReturnData): void {
    return;
  }
  override async doRowActionDelete(): Promise<boolean> {
    return true;
  }
  override afterDetailTabActiveItemChange(): void {
    return;
  }
  override initStaticOptions(): void {
    return;
  }
  override async initFormData(): Promise<any> {
    const data = this.dynamicDialogConfig.data;
    if (data?.itemNumber) {
      const items = await lastValueFrom(
        this.commonService.getItemsWithDesc(data.itemNumber, data.inventoryItemId).pipe(take(1))
      ).catch(() => []);
      const match = items.find((i: any) => i.value === data.itemNumber || i.label === data.itemNumber);
      if (match) {
        this.resolvedInventoryItemId = match.id ? Number(match.id) : 0;
        this.resolvedBrand = (match as any).brand ?? '';
        if (match.description) {
          this.formGroup.patchValue({ itemDesc: match.description });
        }
      }
    }
    return true;
  }
  override afterFormDataLoadSuccess(): void {
    const data = this.dynamicDialogConfig.data;
    if (!data || !data.itemNumber) return;

    const patches: any = {};
    if (data.itemNumber) {
      patches.itemNumber = {
        id: this.resolvedInventoryItemId || data.inventoryItemId || data.itemNumber,
        value: data.itemNumber,
        label: data.itemNumber,
      };
    }

    this.formGroup.patchValue(patches);
    if (data.itemNumber) {
      this.formControl('endCustomer').enable();
    }
    this.changeDetectorRef.detectChanges();
  }
  override afterFormDataLoadFail(): void {
    return;
  }
  override initFormControlDisabled(): void {
    this.formControl('invOrg').disable();
    this.formControl('endCustomer').disable();
  }

  override searchReset(): void {
    super.searchReset();
    this.invOrgSuggestions = [];
    this.endCustomerSuggestions = [];
    this.resolvedBrand = '';
    this.formControl('invOrg').disable();
    this.formControl('endCustomer').disable();
  }

  override afterLangChange(): void {
    return;
  }

  // --- AutoComplete search methods ---

  searchReqOuSuggestions(event: { query: any }): void {
    if (!this.reqOuSubject$.observed) {
      this.reqOuSubject$
        .pipe(
          switchMap((keyword) => this.commonService.getOuOrgs(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe((data) => {
          this.reqOuSuggestions = data;
        });
    }
    this.reqOuSubject$.next(event.query);
  }

  searchInvOuSuggestions(event: { query: any }): void {
    if (!this.invOuSubject$.observed) {
      this.invOuSubject$
        .pipe(
          switchMap((keyword) => this.commonService.getOuOrgs(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe((data) => {
          this.invOuSuggestions = data;
        });
    }
    this.invOuSubject$.next(event.query);
  }

  searchCtmrSuggestions(event: { query: any }): void {
    if (!this.ctmrSubject$.observed) {
      this.ctmrSubject$
        .pipe(
          filter(Boolean),
          switchMap((keyword: string) => this.commonService.getCustomersByNumAndName(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe({
          next: (data) => { this.ctmrSuggestions = data; },
          error: () => { this.ctmrSuggestions = []; },
        });
    }
    this.ctmrSubject$.next(event.query);
  }

  searchItemSuggestions(event: { query: any }): void {
    if (!this.itemSubject$.observed) {
      this.itemSubject$
        .pipe(
          filter(Boolean),
          switchMap((keyword: string) => this.commonService.getItemsWithDesc(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe({
          next: (data) => { this.itemSuggestions = data; },
          error: () => { this.itemSuggestions = []; },
        });
    }
    this.itemSubject$.next(event.query);
  }

  onItemSelect(event: { value: ItemOption }): void {
    const selected = event.value;
    if (selected?.description) {
      this.formGroup.patchValue({ itemDesc: selected.description });
    } else {
      this.formGroup.patchValue({ itemDesc: '' });
    }
    if (selected?.brand) {
      this.resolvedBrand = selected.brand;
    }
    // Clear end customer when item changes, then enable it
    this.formGroup.patchValue({ endCustomer: '' });
    this.endCustomerSuggestions = [];
    this.formControl('endCustomer').enable();
    this.autoCompleteCompleted('autoCompleteCode');
  }

  onItemClear(): void {
    this.formGroup.patchValue({ itemDesc: '', endCustomer: '' });
    this.resolvedBrand = '';
    this.endCustomerSuggestions = [];
    this.formControl('endCustomer').disable();
    this.autoCompleteCleared('autoCompleteCode');
  }

  // INV Org: load based on current Inv OU value
  async loadInvOrgSuggestions(): Promise<void> {
    const invOuValue = this.formControl('invOu').value;
    if (!invOuValue?.id) {
      return;
    }
    const invOuId = Number(invOuValue.id);
    try {
      const data = await lastValueFrom(this.commonService.getOrganizations(invOuId).pipe(take(1)));
      this.invOrgSuggestions = (data ?? []).map(org => ({
        id: String(org.organizationId ?? org.organizationCode),
        value: String(org.organizationId ?? org.organizationCode),
        label: `${org.organizationCode} - ${org.organizationName}`,
      }));
    } catch {
      this.invOrgSuggestions = [];
    }
  }

  onInvOuChange(): void {
    // Clear INV Org when Inv OU changes, then enable it
    this.formGroup.patchValue({ invOrg: '' });
    this.invOrgSuggestions = [];
    this.formControl('invOrg').enable();
    this.autoCompleteCompleted('autoCompleteCode');
  }

  onInvOuClear(): void {
    // Disable INV Org when Inv OU is cleared
    this.formGroup.patchValue({ invOrg: '' });
    this.invOrgSuggestions = [];
    this.formControl('invOrg').disable();
    this.autoCompleteCleared('autoCompleteCode');
  }

  // End Customer: load based on current Item Number and OU values
  async loadEndCustomerSuggestions(): Promise<void> {
    const itemValue = this.formControl('itemNumber').value;
    if (!itemValue?.value) {
      return;
    }

    const reqOuValue = this.formControl('reqOu').value;
    const invOuValue = this.formControl('invOu').value;
    const reqOuId = reqOuValue?.id ? Number(reqOuValue.id) : 0;
    const invOuId = invOuValue?.id ? Number(invOuValue.id) : 0;
    const brand = this.resolvedBrand;
    const uuid = crypto.randomUUID();

    if (!reqOuId || !invOuId) {
      this.systemMessageService.warn('message.detail.req-ou-and-inv-ou-required-first');
      return;
    }

    try {
      // Get corpGroup for both Request OU and Inv OU
      const [reqOuOrg, invOuOrg] = await lastValueFrom(
        forkJoin([
          this.commonService.getOuOrg(reqOuId).pipe(take(1)),
          this.commonService.getOuOrg(invOuId).pipe(take(1)),
        ])
      );

      const reqCorpGroup = reqOuOrg.corpGroup;
      const invCorpGroup = invOuOrg.corpGroup;

      const res = await lastValueFrom(
        this.commonService.getItpEndCustomers({
          brand,
          reqOuId,
          reqCorpGroup,
          invOuId,
          invCorpGroup,
          uuid,
        }).pipe(take(1))
      );

      this.endCustomerSuggestions = (res.responseData ?? []).map(item => item.endCustomer);
    } catch {
      this.endCustomerSuggestions = [];
    }
  }

  clearEndCustomer(): void {
    this.formGroup.patchValue({ endCustomer: '' });
  }
}
